import { useEffect, useRef } from "react";
import {
  EIGHTBALL_SOLID_COLORS,
  EIGHTBALL_STRIPE_COLORS,
  POOL,
  POOL_CUSHIONS,
  POOL_FOOT_SPOT,
  POOL_HEAD_SPOT,
  POOL_PLAY,
  POOL_POCKETS,
  aimContact,
  findPoolBall,
  type EightballSnapshot,
  type PoolBall,
} from "@ziolo/shared";

const BASE_W = POOL.TABLE_W;
const BASE_H = POOL.TABLE_H;
const BALL_R = POOL.BALL_R;

export interface EightballTableProps {
  state: EightballSnapshot;
  mySeat: number;
  power: number;
  aimAngle: number;
  ghost: { x: number; y: number; active: boolean };
  onAim: (angleDeg: number) => void;
  onPlace: (x: number, y: number) => void;
  onStrike: () => void;
  onGhost: (x: number, y: number, active: boolean) => void;
  onPowerDelta: (delta: number) => void;
}

function cssToTable(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
  const rect = canvas.getBoundingClientRect();
  const x = ((clientX - rect.left) / rect.width) * BASE_W;
  const y = ((clientY - rect.top) / rect.height) * BASE_H;
  return { x, y };
}

function ballColor(id: number): string {
  if (id === 0) return "#f5f5f5";
  if (id === 8) return "#111111";
  return EIGHTBALL_SOLID_COLORS[id] ?? EIGHTBALL_STRIPE_COLORS[id] ?? "#888";
}

function drawDiamond(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.beginPath();
  ctx.moveTo(x, y - 4);
  ctx.lineTo(x + 4, y);
  ctx.lineTo(x, y + 4);
  ctx.lineTo(x - 4, y);
  ctx.closePath();
  ctx.fill();
}

function drawTable(ctx: CanvasRenderingContext2D) {
  ctx.clearRect(0, 0, BASE_W, BASE_H);
  ctx.fillStyle = "#2a2420";
  ctx.fillRect(0, 0, BASE_W, BASE_H);
  ctx.fillStyle = "#6b3e1f";
  ctx.fillRect(0, 0, BASE_W, BASE_H);

  ctx.fillStyle = "#2f7a52";
  ctx.fillRect(POOL_PLAY.left, POOL_PLAY.top, POOL_PLAY.width, POOL_PLAY.height);

  ctx.strokeStyle = "#246644";
  ctx.lineWidth = 2;
  ctx.strokeRect(POOL_PLAY.left + 1, POOL_PLAY.top + 1, POOL_PLAY.width - 2, POOL_PLAY.height - 2);

  ctx.strokeStyle = "#3d9a68";
  ctx.lineWidth = 14;
  ctx.lineCap = "round";
  for (const seg of POOL_CUSHIONS) {
    ctx.beginPath();
    ctx.moveTo(seg.x1, seg.y1);
    ctx.lineTo(seg.x2, seg.y2);
    ctx.stroke();
  }

  ctx.fillStyle = "#0a0a0a";
  for (const pocket of POOL_POCKETS) {
    ctx.beginPath();
    ctx.arc(pocket.x, pocket.y, pocket.r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = "rgba(255,255,255,0.7)";
  for (let i = 1; i <= 7; i++) {
    if (i === 4) continue;
    const x = POOL_PLAY.left + (POOL_PLAY.width / 8) * i;
    drawDiamond(ctx, x, POOL_PLAY.top - 14);
    drawDiamond(ctx, x, POOL_PLAY.bottom + 14);
  }
  for (let i = 1; i <= 3; i++) {
    const y = POOL_PLAY.top + (POOL_PLAY.height / 4) * i;
    drawDiamond(ctx, POOL_PLAY.left - 14, y);
    drawDiamond(ctx, POOL_PLAY.right + 14, y);
  }

  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.beginPath();
  ctx.arc(POOL_HEAD_SPOT.x, POOL_HEAD_SPOT.y, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(POOL_FOOT_SPOT.x, POOL_FOOT_SPOT.y, 2.2, 0, Math.PI * 2);
  ctx.fill();
}

function drawBallNumber(ctx: CanvasRenderingContext2D, id: number, x: number, y: number, invert: boolean) {
  if (id !== 8) {
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(x, y, BALL_R * 0.45, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = invert ? "#fff" : "#111";
  ctx.font = "bold 9px ui-sans-serif, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(id), x, y + 0.5);
}

function drawBall(ctx: CanvasRenderingContext2D, ball: PoolBall) {
  const { id, x, y } = ball;
  if (id === 0) {
    ctx.fillStyle = "#f7f7f7";
    ctx.beginPath();
    ctx.arc(x, y, BALL_R, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#cfcfcf";
    ctx.lineWidth = 0.6;
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,0.45)";
    ctx.beginPath();
    ctx.arc(x - 3, y - 3, BALL_R * 0.28, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  if (id === 8) {
    ctx.fillStyle = "#111";
    ctx.beginPath();
    ctx.arc(x, y, BALL_R, 0, Math.PI * 2);
    ctx.fill();
    drawBallNumber(ctx, id, x, y, true);
    return;
  }

  const stripe = id >= 9;
  const color = ballColor(id);
  if (stripe) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, BALL_R, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = "#fff";
    ctx.fillRect(x - BALL_R, y - BALL_R, 2 * BALL_R, 2 * BALL_R);
    ctx.fillStyle = color;
    ctx.fillRect(x - BALL_R, y - BALL_R * 0.6, 2 * BALL_R, BALL_R * 1.2);
    ctx.restore();
    ctx.beginPath();
    ctx.arc(x, y, BALL_R, 0, Math.PI * 2);
    ctx.strokeStyle = "#ccc";
    ctx.lineWidth = 0.5;
    ctx.stroke();
    drawBallNumber(ctx, id, x, y, false);
  } else {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, BALL_R, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
    drawBallNumber(ctx, id, x, y, false);
  }
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.beginPath();
  ctx.arc(x - 3, y - 3, BALL_R * 0.28, 0, Math.PI * 2);
  ctx.fill();
}

function drawAim(ctx: CanvasRenderingContext2D, state: EightballSnapshot, mySeat: number, aimAngle: number) {
  const canDraw =
    !state.simRunning &&
    !state.gameEnded &&
    mySeat === state.currentTurn &&
    !state.ballInHand;
  if (!canDraw) return;
  const info = aimContact(state.balls, aimAngle);
  if (!info) return;

  ctx.setLineDash([6, 6]);
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(info.cueSurf.x, info.cueSurf.y);
  if (info.hasHit && info.contact) {
    ctx.lineTo(info.contact.x, info.contact.y);
  } else {
    const a = (aimAngle * Math.PI) / 180;
    ctx.lineTo(info.cueSurf.x + Math.cos(a) * 220, info.cueSurf.y + Math.sin(a) * 220);
  }
  ctx.stroke();
  ctx.setLineDash([]);

  if (info.hasHit && info.contact && info.objDir) {
    const Lobj = 110 * Math.max(0.35, info.normalMag);
    ctx.setLineDash([3, 6]);
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.beginPath();
    ctx.moveTo(info.contact.x, info.contact.y);
    ctx.lineTo(info.contact.x + info.objDir.x * Lobj, info.contact.y + info.objDir.y * Lobj);
    ctx.stroke();
    if (info.cueTangent) {
      const Lcue = 190 * info.tangentMag;
      if (Lcue > 2) {
        ctx.setLineDash([2, 5]);
        ctx.strokeStyle = "rgba(255,255,255,0.5)";
        ctx.beginPath();
        ctx.moveTo(info.contact.x, info.contact.y);
        ctx.lineTo(info.contact.x + info.cueTangent.x * Lcue, info.contact.y + info.cueTangent.y * Lcue);
        ctx.stroke();
      }
    }
    ctx.setLineDash([]);
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(info.contact.x, info.contact.y, 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function EightballTable({
  state,
  mySeat,
  power,
  aimAngle,
  ghost,
  onAim,
  onPlace,
  onStrike,
  onGhost,
  onPowerDelta,
}: EightballTableProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef(state);
  const aimRef = useRef(aimAngle);
  const mySeatRef = useRef(mySeat);
  const ghostRef = useRef(ghost);
  const displayRef = useRef<PoolBall[]>([]);

  stateRef.current = state;
  aimRef.current = aimAngle;
  mySeatRef.current = mySeat;
  ghostRef.current = ghost;

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      const cssW = Math.min(960, Math.max(320, wrap.clientWidth));
      const cssH = Math.round(cssW * (BASE_H / BASE_W));
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      ctx.setTransform((dpr * cssW) / BASE_W, 0, 0, (dpr * cssH) / BASE_H, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    let frame = 0;
    const tick = () => {
      const snap = stateRef.current;
      const targets = snap.balls;
      if (displayRef.current.length !== targets.length) {
        displayRef.current = targets.map((ball) => ({ ...ball }));
      } else {
        for (let i = 0; i < targets.length; i++) {
          const t = targets[i];
          const d = displayRef.current[i];
          if (!t || !d) continue;
          if (t.id !== d.id || t.pocketed || d.pocketed) {
            displayRef.current[i] = { ...t };
            continue;
          }
          const k = snap.simRunning ? 0.55 : 1;
          d.x += (t.x - d.x) * k;
          d.y += (t.y - d.y) * k;
          d.vx = t.vx;
          d.vy = t.vy;
          d.pocketed = t.pocketed;
        }
      }
      drawTable(ctx);
      const g = ghostRef.current;
      if (g.active) {
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(g.x, g.y, BALL_R, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      for (const ball of displayRef.current) {
        if (!ball.pocketed) drawBall(ctx, ball);
      }
      const drawn = {
        ...snap,
        balls: displayRef.current.length ? displayRef.current : snap.balls,
      };
      drawAim(ctx, drawn, mySeatRef.current, aimRef.current);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);

  const myTurn = mySeat === state.currentTurn && mySeat >= 0;

  return (
    <div className="eightball-table" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="eightball-table__canvas"
        data-testid="eightball-canvas"
        width={BASE_W}
        height={BASE_H}
        onPointerMove={(event) => {
          const canvas = canvasRef.current;
          if (!canvas || state.gameEnded) return;
          const p = cssToTable(canvas, event.clientX, event.clientY);
          if (state.ballInHand && myTurn) {
            onGhost(p.x, p.y, true);
            return;
          }
          const cue = findPoolBall(state.balls, 0);
          if (!cue || cue.pocketed) return;
          onAim((Math.atan2(p.y - cue.y, p.x - cue.x) * 180) / Math.PI);
        }}
        onPointerLeave={() => onGhost(0, 0, false)}
        onPointerDown={(event) => {
          const canvas = canvasRef.current;
          if (!canvas || state.gameEnded || !myTurn) return;
          canvas.setPointerCapture(event.pointerId);
        }}
        onClick={(event) => {
          const canvas = canvasRef.current;
          if (!canvas || state.gameEnded || !myTurn) return;
          const p = cssToTable(canvas, event.clientX, event.clientY);
          if (state.ballInHand) {
            onPlace(p.x, p.y);
            onGhost(0, 0, false);
            return;
          }
          if (!state.simRunning) onStrike();
        }}
        onWheel={(event) => {
          event.preventDefault();
          onPowerDelta(Math.sign(-event.deltaY) * 5);
        }}
      />
      <p className="eightball-table__power" data-testid="eightball-power">
        Siła {Math.round(power)}%
      </p>
    </div>
  );
}
